import axios from "axios";
import Bottleneck from "bottleneck";
import { baseAxios } from "../apis";

export const extractErrorMessage = (errorObj) => {
  // Helper: check if a string contains HTML tags
  const containsHTML = (str) => /<\/?[a-z][\s\S]*>/i.test(str);

  // If error is a string
  if (typeof errorObj === "string") {
    return containsHTML(errorObj)
      ? "Something went wrong. Please try again later."
      : errorObj;
  }

  // If error is an array (like ['message 1', 'message 2'])
  if (Array.isArray(errorObj)) {
    return (
      errorObj.filter((e) => typeof e === "string").join(", ") ||
      extractErrorMessage(errorObj[0])
    );
  }

  // If error is an HTML element
  if (
    typeof HTMLElement === "object"
      ? errorObj instanceof HTMLElement
      : errorObj &&
        typeof errorObj === "object" &&
        errorObj !== null &&
        errorObj.nodeType === 1 &&
        typeof errorObj.nodeName === "string"
  ) {
    return "Something went wrong. Please try again later.";
  }

  // If error is an object, recurse its values
  if (typeof errorObj === "object" && errorObj !== null) {
    for (const key in errorObj) {
      const message = extractErrorMessage(errorObj[key]);
      if (message) return message;
    }
  }

  // This part of the function handles the error you provided
  if (errorObj?.message) {
    return errorObj.message;
  }

  // Default fallback message
  return "Something went wrong. Please try again later.";
};

// --- Rate Limiter Configuration ---
const limiter = new Bottleneck({
  maxConcurrent: 2, // Allow 2 concurrent requests
  minTime: 500, // Wait at least 500ms between each request
});

// Wrap axios.get with rate limiter
const rateLimitedAxiosGet = limiter.wrap(axios.get);
const rateLimitedBaseAxiosGet = limiter.wrap(baseAxios.get);

// --- Two-Layer Cache ---
const coinCache = new Map();

const getFromLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    const parsed = JSON.parse(item);

    // Check if cache is expired (24 hours)
    const now = Date.now();
    if (parsed.expiry && now > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }

    return parsed.data;
  } catch (error) {
    console.error("Error reading from localStorage:", error);
    return null;
  }
};

const saveToLocalStorage = (key, data) => {
  try {
    const item = {
      data,
      expiry: Date.now() + 24 * 60 * 60 * 1000, // 24 hours
    };
    localStorage.setItem(key, JSON.stringify(item));
  } catch (error) {
    console.error("Error saving to localStorage:", error);
  }
};

// --- Queue for batch processing ---
const pendingRequests = new Map();
let batchTimeout = null;

/**
 * Batch fetch multiple coin images at once - ONE REQUEST PER SYMBOL
 * @param {string[]} symbols - Array of coin symbols
 * @returns {Promise<Object>} - Object mapping symbols to image URLs
 */
const batchFetchCoins = async (symbols) => {
  if (!symbols || symbols.length === 0) return {};

  const uniqueSymbols = [...new Set(symbols.map((s) => s.toLowerCase()))];
  const results = {};

  // Filter out symbols that are already cached
  const uncachedSymbols = uniqueSymbols.filter((symbol) => {
    const cacheKey = `coin_image_${symbol}`;

    // Check in-memory cache
    if (coinCache.has(symbol)) {
      results[symbol] = coinCache.get(symbol);
      return false;
    }

    // Check localStorage cache
    const localImage = getFromLocalStorage(cacheKey);
    if (localImage) {
      coinCache.set(symbol, localImage);
      results[symbol] = localImage;
      return false;
    }

    return true;
  });

  if (uncachedSymbols.length === 0) {
    return results;
  }

  // Process each symbol individually (backend doesn't support batch)
  for (const symbol of uncachedSymbols) {
    try {
      const backendRes = await rateLimitedBaseAxiosGet(`/api/get_coin_image`, {
        params: {
          symbol: symbol.toUpperCase(),
        },
        timeout: 5000, // 5 second timeout
      });

      // Process backend response - single object
      if (backendRes.data && backendRes.data.image_url) {
        const img = backendRes.data.image_url;
        results[symbol] = img;
        coinCache.set(symbol, img);
        saveToLocalStorage(`coin_image_${symbol}`, img);
      } else {
        results[symbol] = null;
        coinCache.set(symbol, null);
      }
    } catch (backendErr) {
      console.warn(
        `Backend fetch failed for ${symbol}, falling back to CoinGecko:`,
        backendErr.message,
      );

      // Fallback to CoinGecko for this symbol
      try {
        const res = await rateLimitedAxiosGet(
          `https://api.coingecko.com/api/v3/coins/markets`,
          {
            params: {
              vs_currency: "usd",
              per_page: 250,
              page: 1,
              sparkline: false,
            },
            timeout: 10000, // 10 second timeout
          },
        );

        const coin = res.data.find((c) => c?.symbol?.toLowerCase() === symbol);
        const image = coin?.image || null;
        results[symbol] = image;
        coinCache.set(symbol, image);
        if (image) {
          saveToLocalStorage(`coin_image_${symbol}`, image);
        }
      } catch (err) {
        console.error(`CoinGecko fallback failed for ${symbol}:`, err.message);
        results[symbol] = null;
        coinCache.set(symbol, null);
      }
    }
  }

  return results;
};

/**
 * Fetches a coin's image URL by its symbol with intelligent batching
 * @param {string} symbol - The coin's ticker symbol
 * @returns {Promise<string|null>} - The URL of the coin's image or null
 */
export const getAssetCoin = async (symbol) => {
  if (!symbol) return null;

  const lowerSymbol = symbol.toLowerCase();
  const cacheKey = `coin_image_${lowerSymbol}`;

  // Check 1: In-memory cache (fastest)
  if (coinCache.has(lowerSymbol)) {
    return coinCache.get(lowerSymbol);
  }

  // Check 2: localStorage cache (persistent)
  const localImage = getFromLocalStorage(cacheKey);
  if (localImage) {
    coinCache.set(lowerSymbol, localImage);
    return localImage;
  }

  // Check if there's already a pending request for this symbol
  if (pendingRequests.has(lowerSymbol)) {
    const existing = pendingRequests.get(lowerSymbol);
    return existing.promise;
  }

  // Create a promise for this symbol
  let resolveFunc;
  const promise = new Promise((resolve) => {
    resolveFunc = resolve;
  });

  // Store the promise and resolve function
  pendingRequests.set(lowerSymbol, { promise, resolve: resolveFunc });

  // Clear existing timeout
  if (batchTimeout) {
    clearTimeout(batchTimeout);
  }

  // Set new timeout to batch requests
  batchTimeout = setTimeout(async () => {
    const symbolsToFetch = Array.from(pendingRequests.keys());
    const entries = Array.from(pendingRequests.values());

    // Clear pending requests
    pendingRequests.clear();

    // Batch fetch
    const results = await batchFetchCoins(symbolsToFetch);

    // Resolve all promises
    symbolsToFetch.forEach((sym, idx) => {
      const resolveFunction = entries[idx].resolve;
      if (typeof resolveFunction === "function") {
        resolveFunction(results[sym] || null);
      }
    });
  }, 100); // Wait 100ms to collect more requests

  return promise;
};

/**
 * Preload coin images for an array of symbols
 * Useful for loading images before they're needed in the UI
 * @param {string[]} symbols - Array of coin symbols to preload
 */
export const preloadCoinImages = async (symbols) => {
  if (!symbols || symbols.length === 0) return;

  const uniqueSymbols = [...new Set(symbols.map((s) => s.toLowerCase()))];
  await batchFetchCoins(uniqueSymbols);
};

/**
 * Clear the coin image cache
 * Useful for forcing a refresh of coin data
 */
export const clearCoinCache = () => {
  coinCache.clear();
  // Clear localStorage cache
  const keys = Object.keys(localStorage);
  keys.forEach((key) => {
    if (key.startsWith("coin_image_")) {
      localStorage.removeItem(key);
    }
  });
};
// export const getAssetName = (token) => {
//   // Return an empty string if the token is not a valid string
//   if (!token || typeof token !== "string") {
//     return "";
//   }

//   // Split, get the first part, and convert to uppercase
//   return token.split(/[/_-]/)[0].toUpperCase();
// };

const QUOTE_CURRENCIES = [
  // Stablecoins / crypto quotes
  "USDT",
  "USDC",
  "BUSD",
  "DAI",
  "BTC",
  "ETH",
  "BNB",

  // Major fiat
  "USD",
  "EUR",
  "GBP",
  "JPY",
  "CNY",
  "CHF",
  "AUD",
  "CAD",
  "NZD",

  // Asia
  "SGD",
  "HKD",
  "IDR",
  "MYR",
  "THB",
  "PHP",
  "VND",
  "KRW",
  "INR",

  // Middle East
  "AED",
  "SAR",
  "QAR",
  "KWD",

  // Europe
  "SEK",
  "NOK",
  "DKK",
  "PLN",
  "CZK",
  "HUF",
  "RON",

  // Latin America
  "BRL",
  "MXN",
  "ARS",
  "CLP",
  "COP",
  "PEN",

  // Africa
  "ZAR",
  "NGN",
  "KES",
  "GHS",
];

export const getAssetName = (token) => {
  if (!token || typeof token !== "string") return "";

  const normalized = token.trim().toUpperCase();

  // Token is itself a currency
  if (QUOTE_CURRENCIES.includes(normalized)) {
    return normalized;
  }

  // BTC/SGD, BTC-SGD, BTC_SGD
  const parts = normalized.split(/[/_-]/);
  if (parts.length > 1) {
    return parts[0];
  }

  // BTCUSDT, ETHSGD, SOLJPY
  for (const quote of QUOTE_CURRENCIES) {
    if (normalized.endsWith(quote)) {
      return normalized.slice(0, -quote.length);
    }
  }

  return normalized;
};

export const normalizePair = (token) => {
  if (!token || typeof token !== "string") return "";

  const normalized = token.trim().toUpperCase();

  //  Already separated: BTC/USDT, BTC-USDT, BTC_USDT
  const splitMatch = normalized.split(/[/_-]/);
  if (splitMatch.length === 2) {
    return `${splitMatch[0]}/${splitMatch[1]}`;
  }

  //  Concatenated: BTCUSDT, ETHIDR, SOLJPY
  for (const quote of QUOTE_CURRENCIES.sort((a, b) => b.length - a.length)) {
    if (normalized.endsWith(quote)) {
      const base = normalized.slice(0, -quote.length);
      if (base) {
        return `${base}/${quote}`;
      }
    }
  }

  //  Fallback (unknown format)
  return normalized;
};

export function normalizeBotName(value) {
  if (!value) return value;

  if (value === "XENO ARC") {
    return "XENOARC";
  }

  return value;
}
